import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, TrendingUp, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, IconChip } from "@/components/ui/card";
import { STATUS_SHAPES, type StatusKey } from "@/lib/status";
import { ApiError, getTrends, type Trends as TrendsData } from "@/lib/api";

type Range = "day" | "week" | "month";
const RANGES: { key: Range; label: string }[] = [
  { key: "day", label: "Day" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
];
const SUMMARY_LABEL: Record<StatusKey, string> = {
  green: "On Track",
  yellow: "Caution",
  red: "Needs Attention",
};
const BAR_TINT: Record<StatusKey, string> = {
  green: "bg-status-green",
  yellow: "bg-status-yellow",
  red: "bg-status-red",
};

/** Weekday letter from a UTC YYYY-MM-DD, rendered in local calendar terms. */
function weekday(dateISO: string): string {
  const [y, m, d] = dateISO.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "short" });
}
function shortDate(dateISO: string): string {
  const [y, m, d] = dateISO.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function Trends() {
  const navigate = useNavigate();
  const [range, setRange] = useState<Range>("week");
  const [data, setData] = useState<TrendsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    getTrends(range)
      .then(setData)
      .catch((e) => {
        if (e instanceof ApiError && e.status === 401) navigate("/signin");
        else setError("Couldn't load your trends.");
      })
      .finally(() => setLoading(false));
  }, [range, navigate]);

  const maxItems = data ? Math.max(1, ...data.days.map((d) => d.item_count)) : 1;
  const showDayLabels = range === "week";

  return (
    <div className="pb-16">
      <div className="flex items-center gap-2 px-4 pb-2 pt-6">
        <Button variant="ghost" size="icon" aria-label="Back" onClick={() => navigate("/app")}>
          <ArrowLeft className="h-5 w-5" strokeWidth={2} />
        </Button>
        <h1 className="flex-1 text-lg font-semibold text-foreground">Trends</h1>
      </div>

      <div className="flex flex-col gap-3 px-6 pt-2">
        {/* Segmented range control */}
        <div className="flex gap-0.5 rounded-lg bg-muted p-1">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setRange(r.key)}
              className={`flex-1 rounded-md py-1.5 text-[13px] transition-colors ${
                range === r.key
                  ? "bg-card font-semibold text-foreground shadow-sm"
                  : "font-medium text-muted-foreground"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2} />
            <span className="text-sm">Loading…</span>
          </div>
        ) : error || !data ? (
          <p className="py-16 text-center text-sm text-muted-foreground">{error}</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-2">
              {(["green", "yellow", "red"] as StatusKey[]).map((s) => {
                const Shape = STATUS_SHAPES[s];
                return (
                  <Badge key={s} status={s}>
                    <Shape className="h-[11px] w-[11px]" strokeWidth={2} />
                    {data.summary[s]} {SUMMARY_LABEL[s]}
                  </Badge>
                );
              })}
            </div>

            <Card>
              <CardContent className="p-4">
                <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                  {range === "day" ? "Today" : range === "week" ? "This Week" : "This Month"}
                </p>
                <div className="mt-3.5 flex h-[120px] items-end gap-1.5">
                  {data.days.map((d) => {
                    const h = d.item_count > 0 ? Math.round((d.item_count / maxItems) * 100) : 0;
                    return (
                      <div key={d.date} className="flex h-full flex-1 flex-col items-center justify-end gap-1.5">
                        <div
                          className={`w-full rounded ${d.status ? BAR_TINT[d.status] : "bg-muted"}`}
                          style={{ height: `${d.status ? Math.max(h, 8) : 4}%` }}
                          title={`${d.date}: ${d.status ?? "no check-in"}`}
                        />
                        {showDayLabels && (
                          <span className="text-[11px] text-muted-foreground">{weekday(d.date)}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                {!showDayLabels && data.days.length > 1 && (
                  <div className="mt-1.5 flex justify-between">
                    <span className="text-[11px] text-muted-foreground">{shortDate(data.days[0].date)}</span>
                    <span className="text-[11px] text-muted-foreground">Today</span>
                  </div>
                )}
                <p className="mt-2.5 text-[11px] text-muted-foreground">
                  Bar height = questions answered · color = overall status
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="flex items-start gap-3 px-4 py-3.5">
                <IconChip icon={TrendingUp} tint="primary" />
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                    Pattern
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-foreground">{data.pattern}</p>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
